/**
 * HelpDrawer - contextual help (Story #5351 W1-T9, Screen Contract G07:
 * "Header Help and every route/control/warning/state help trigger").
 *
 * Five stable sections per the OKF & Help tab contract: On this page /
 * Guided tasks / Troubleshoot / Reference / Get help. Content resolves from
 * the current route via resolveHelpForLocation (help/w1/manifest.ts) —
 * whatever screen the operator has open is what they see help for, with no
 * separate navigation. Read-only by construction: this component makes no
 * write API calls of any kind (verified by the task's mandatory grep gate
 * over this directory and help/w1/),
 * so it can never execute an action on the operator's behalf — the mockup's
 * "opens preflight links but NEVER executes actions" rule holds because
 * there is nothing here capable of doing otherwise.
 *
 * G07's own route note says Help "opens as drawer or narrow-screen sheet
 * without navigation" — unlike Jobs/Approvals/Alerts (G04-G06), which are
 * URL-driven (`?drawer=1&...`), Help's open/closed state is NOT put in the
 * URL; the caller (Stream 7's shell closure) owns an `open` boolean, same as
 * any other locally-controlled overlay.
 *
 * Deliberately NOT mounted into Header/Layout by this task — those files
 * are components/layout/*, outside this task's file ownership (same
 * handoff ScopeRibbon.tsx recorded for Stream 7). See the PR body.
 *
 * W6 content (Story #5483, Task #5529-#5533 Stream 12 follow-up): W1's
 * resolver only ever covers Home/global-shell routes, so a second resolver
 * for the E0x/R0x environment/release routes is tried as a fallback when W1
 * has nothing for the current path -- one drawer, two wave-scoped content
 * sources, same "no page-specific help published" empty state either way.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import Drawer from '../ui/Drawer';
import StatePanel from '../ui/StatePanel';
import { resolveHelpForLocation } from '../../help/w1/manifest';
import { resolveW6HelpForLocation } from '../../help/w6/manifest';
import { isW1Shell } from '../../utils/w1Flag';
import PortalsHelpDrawer from './PortalsHelpDrawer';

export interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{children}</h3>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const NumberedList: React.FC<{ items: string[] }> = ({ items }) => (
  <ol className="space-y-1.5 text-sm text-gray-700 list-decimal list-inside">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ol>
);

const HelpDrawer: React.FC<HelpDrawerProps> = ({ open, onClose }) => {
  const location = useLocation();

  // Render-time flag read (never module scope — see utils/w1Flag.ts).
  if (!isW1Shell() || !open) return null;

  const entry = resolveHelpForLocation(location.pathname, location.search)
    ?? resolveW6HelpForLocation(location.pathname, location.search);

  // P34 is the Portals workspace's warning-aware implementation of the W1
  // drawer contract. It remains mounted by Layout, so opening/closing it does
  // not navigate or replace the underlying portal route.
  const portalsWorkspaceEnabled = (window.SECUREEMBED_CONFIG as typeof window.SECUREEMBED_CONFIG & {
    PORTALS_WORKSPACE_ENABLED?: boolean;
  })?.PORTALS_WORKSPACE_ENABLED === true;
  if (portalsWorkspaceEnabled && /^\/portals\/[^/]+\//.test(location.pathname)) {
    return <PortalsHelpDrawer onClose={onClose} />;
  }

  return (
    <Drawer onClose={onClose} ariaLabel="Contextual help" maxWidthClassName="max-w-md" panelClassName="bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <h2 className="text-base font-semibold text-gray-900">Help</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close help"
          className="p-1 rounded hover:bg-gray-100 text-gray-500"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {entry ? (
          <>
            <section className="px-4 py-4 border-b border-gray-100">
              <SectionHeading>On this page</SectionHeading>
              <BulletList items={entry.onThisPage} />
            </section>
            <section className="px-4 py-4 border-b border-gray-100">
              <SectionHeading>Guided tasks</SectionHeading>
              <NumberedList items={entry.guidedTasks} />
            </section>
            <section className="px-4 py-4 border-b border-gray-100">
              <SectionHeading>Troubleshoot</SectionHeading>
              <BulletList items={entry.troubleshoot} />
            </section>
            <section className="px-4 py-4 border-b border-gray-100">
              <SectionHeading>Reference</SectionHeading>
              <p className="text-sm text-gray-700">{entry.reference}</p>
            </section>
            <section className="px-4 py-4">
              <SectionHeading>Get help</SectionHeading>
              <p className="text-sm text-gray-700">{entry.owner}</p>
              <p className="text-xs text-gray-500 mt-1">
                Report or escalate through the owner named above.
              </p>
            </section>
          </>
        ) : (
          <div className="px-4 py-4">
            <StatePanel
              state="empty"
              message="No page-specific help is published for this route yet."
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default HelpDrawer;
